import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { getHours, getMinutes } from "date-fns";
import { type ReactNode } from "react";

import { TimePicker } from "../components/TimePicker";

export interface Duration {
  hours: number;
  minutes: number;
}

interface DurationInputProps {
  value?: Duration;
  onChange: (duration: Duration) => void;
  maxHours?: number;
  minutesStep?: number;
  showFormattedDuration?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  sx?: Record<string, any>;
  /**
   * Useful when DurationInput is nested within a parent that also captures touch events
   * If `true`, `event.stopPropagation()` will be called immediately on touch capture
   * which will prevent the parent from stealing the input's touch events
   */
  preventParentTouchStart?: boolean;
}

// Helper function to convert Duration to Date for the clock component
function durationToDate(duration: Duration): Date {
  const date = new Date();
  date.setHours(duration.hours, duration.minutes, 0, 0);
  return date;
}

// Helper function to convert Date object back to Duration
function dateToDuration(date: Date | undefined): Duration {
  if (!isDefined(date)) {
    return { hours: 1, minutes: 0 };
  }

  return {
    hours: getHours(date),
    minutes: getMinutes(date),
  };
}

// Helper function to format duration into readable text
export function formatDuration(duration: Duration): string {
  const hourText =
    duration.hours > 0 ? `${duration.hours} hour${duration.hours === 1 ? "" : "s"}` : "";

  const minuteText =
    duration.minutes > 0 ? `${duration.minutes} minute${duration.minutes === 1 ? "" : "s"}` : "";

  const separator = hourText && minuteText ? " " : "";

  return `${hourText}${separator}${minuteText}`;
}

export function minutesToDuration(minutes: number): Duration {
  return {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  };
}

export function durationToMinutes(duration: Duration): number {
  return duration.hours * 60 + duration.minutes;
}

export function DurationInput(props: DurationInputProps) {
  const {
    value = { hours: 1, minutes: 0 },
    onChange,
    maxHours = 12,
    minutesStep = 1,
    showFormattedDuration = true,
    disabled = false,
    children,
    sx,
    preventParentTouchStart,
  } = props;

  // Create max time based on maxHours
  const maxTime = new Date();
  maxTime.setHours(maxHours, 59, 59, 999);

  const handleTimeChange = (newValue: Date | undefined) => {
    if (isDefined(newValue) && !disabled) {
      const duration = dateToDuration(newValue);
      onChange(duration);
    }
  };

  return (
    <Stack spacing={4} alignItems="center" sx={sx}>
      {isDefined(children) && <Box>{children}</Box>}

      <TimePicker
        skipDisabled
        ampm={false}
        preventParentTouchStart={preventParentTouchStart}
        disabled={disabled}
        value={durationToDate(value)}
        views={["hours", "minutes"]}
        maxTime={maxTime}
        timeSteps={{
          hours: 1,
          minutes: minutesStep,
        }}
        sx={{
          justifyContent: "center",
          "& .MuiList-root": {
            scrollbarWidth: "none",
          },
        }}
        onChange={handleTimeChange}
      />

      {showFormattedDuration && (
        <Box sx={{ textAlign: "center" }}>
          <Text semibold variant="h6">
            {formatDuration(value)}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
